import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {UserPreferences} from '@/reducers/user-preferences.reducer';
import type {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import type {TeamCreditsActionProps} from '@Components/team-credits/team-credits-slice';
import type {DesignerAttributionInfo} from '@Components/poster-editor/components/poster-editing-side-panel/components/designer-attribution/designer-attribution.types';
import type {FontData} from '@Libraries/font-library';

export const COPY_STYLES_COOKIE = 'pmwcsc';

export enum ElementToHide {
  DOWNLOAD = 'download',
  FILE = 'file',
  UPGRADE = 'upgrade',
}

/**
 * DOM elements of the poster-editor shell (chrome around the whiteboard), registered on
 * `window.posterEditor.elements`. Only the UI layer references these. Whiteboard-surface
 * elements the domain layer reads live in `WhiteboardDomElement` in poster-whiteboard.
 */
export enum PosterEditorShellElement {
  POSTER_EDITOR_MOBILE_BOTTOM_EDITING_BAR = 'POSTER_EDITOR_MOBILE_BOTTOM_EDITING_BAR',
  POSTER_EDITOR_NAVBAR = 'POSTER_EDITOR_NAVBAR',
  POSTER_EDITOR_WEB_BOTTOM_BAR = 'POSTER_EDITOR_WEB_BOTTOM_BAR',
  EDITOR_WITHOUT_NAV = 'EDITOR_WITHOUT_NAV',
}

export interface PosterEditor {
  whiteboard?: Poster;
  config?: PosterEditorConfig;
  elements: Partial<Record<WhiteboardDomElement | PosterEditorShellElement, JQuery>>;
}

export interface TemplateCreatorProfileInfoSeed {
  templateId: string;
  info: DesignerAttributionInfo;
}

export interface PosterEditorConfig {
  userPreferences: UserPreferences;
  hasPublishedUsingDigitalDisplay: boolean;
  elementsToHide?: ElementToHide[];
  keepFreeDownloadDefault?: boolean;
  nextBtnCaption?: string;
  showSuperUserOptions?: boolean;
  idOrder?: string;
  onboarding?: PosterEditorOnboarding | null;
  hasSeenAiVideo?: boolean;
  teamCredits?: TeamCreditsActionProps;
  templateCreatorProfileInfo?: TemplateCreatorProfileInfoSeed | null;
  pmwFontsVersion: number | null;
  userFonts: FontData[] | null;
  hasRecentStockItems: boolean;
  showMobileUXOnBoarding?: boolean;
  businessProfileUrl?: string;
  isInReview?: boolean;
  isPosterShared?: boolean;
  isPosterSharedInTeam?: boolean;
  featureToShowOnLoad: FeatureToShowOnLoad;
  featureDemo?: FeatureDemo;
  /** Set from the `tl=1` load URL param: the design was just composed on one timeline, so open it once the poster loads. */
  shouldOpenTimelineOnLoad?: boolean;
}

/**
 * Onboarding popups arbitrated on the backend: PosterFacade::getPosterEditorOnboardingToShow()
 * sends at most one per editor load via posterEditorConfig.onboarding. Values mirror the
 * PosterEditorOnboarding PHP enum in Libraries/Facades/Poster/Enums/PosterEditorOnboarding.php.
 */
export enum PosterEditorOnboarding {
  LAYERS = 'layers',
  MULTIPAGE = 'multipage',
  AI_POSTER_ANIMATION = 'aiPosterAnimation',
}

export enum FeatureToShowOnLoad {
  NONE = 'NONE',
  TEMPLATE_POPUP = 'TEMPLATE_POPUP',
  TEXT_POPUP = 'TEXT_POPUP',
  AUDIO_RECORDING = 'AUDIO_RECORDING',
  VIDEO_RECORDING = 'VIDEO_RECORDING',
  AI_VOICE = 'AI_VOICE',
  AI_IMAGE = 'AI_IMAGE',
  AI_TEXT_ART = 'AI_TEXT_ART',
  AI_SUBTITLE = 'AI_SUBTITLE',
  AI_VIDEO = 'AI_VIDEO',
  AI_REMOVE_BG = 'AI_REMOVE_BG',
  AI_EDIT = 'AI_EDIT',
  MENU_MAKER = 'MENU_MAKER',
  STICKERS = 'STICKERS',
}

export enum FeatureDemo {
  NONE = 'NONE',
  MENU_MAKER = 'menu_maker',
  STICKERS = 'stickers',
  ANIMATIONS = 'animations',
  STYLES = 'styles',
  QR_CODE = 'qr_code',
}

export const EDITOR_COLLAPSABLE_SIDEBAR_THRESHOLD = 900;
export const POSTER_EDITOR_NAVBAR_HEIGHT = 60;
